import {
  AfterViewInit,
  Component,
  computed,
  OnDestroy,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  inject,
  OnInit,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { NgxDatatableModule, TableColumn } from '@swimlane/ngx-datatable';
import { AvatarComponent } from '../../../../shared/components/avatar/avatar.component';
import { HttpParams } from '@angular/common/http';
import { Observable, of, Subscription } from 'rxjs';
import { PaginatedResults } from '@bo/ng-api';
import { Card, CardStatus } from '../../../../_interfaces/cards.interface';
import { AppTableComponent } from '../../../../shared/components/table/app-table.component';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import { subscriptionCardsTableColumns } from '../../../../_services/cards-service/cards-utils';
import { CardsSearchOptions } from '../../../../shared/components/forms/search-field/search-utils';
import { CardsFilterComponent } from '../_components/cards-filter/cards-filter.component';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../shared/components/status-badge/status-badge.component';
import { CardsApiService } from '../../../../_services/cards-service/cards-api/cards-api.service';
import { SubscriptionApiService } from '../../../../_services/subscription-service/api/subscription-api-service.service';
import { SubscriptionSelectors } from '../../../../store/subscription/subscription.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { UpgradeToPremiumOverlayComponent } from '../../../../shared/components/upgrade-to-premium-overlay/upgrade-to-premium-overlay.component';
import { TableHeaderTemplateComponent } from '../../../../shared/components/table-header-template/table-header-template.component';
import { ReactiveFormsModule } from '@angular/forms';
import { DatatableComponent } from '@bo/ng-datatable';
import { updateParamsObject } from '../../../../shared/utils/filter-utils';
import { ToggleSwitchComponent } from '../../../../shared/components/forms/toggle-switch/toggle-switch.component';

@Component({
  imports: [
    RouterOutlet,
    AvatarComponent,
    DatePipe,
    CurrencyPipe,
    NgxDatatableModule,
    AppTableComponent,
    EmptyIndicatorPipe,
    CardsFilterComponent,
    CommonModule,
    StatusBadgeComponent,
    UpgradeToPremiumOverlayComponent,
    TableHeaderTemplateComponent,
    ReactiveFormsModule,
    ToggleSwitchComponent,
  ],
  selector: 'app-subscription-cards',
  templateUrl: './subscription-cards.component.html',
})
export class SubscriptionCardsComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly cardsApiService = inject(CardsApiService);
  readonly offcanvasService = inject(OffcanvasService);
  readonly subscriptionApiService = inject(SubscriptionApiService);
  readonly hasSubscriptionCards$ = this.store.select(SubscriptionSelectors.hasSubscriptionCards);
  readonly hasSubscriptionCardsFeature = toSignal(this.hasSubscriptionCards$, { initialValue: false });
  readonly pageParam: Signal<string> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['hideRemoved'] as string;
  });

  filterApplied = signal(false);
  showHideRemoved = signal(true);
  queryParams = new HttpParams();
  columns: Array<TableColumn> = [];
  subscriptions = new Subscription();
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly CardsSearchOptions = CardsSearchOptions;
  private readonly columnsInitialized = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('cardNameCell', { static: false }) cardNameCell?: TemplateRef<any>;
  @ViewChild('expiryCell', { static: false }) expiryCell?: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCell?: TemplateRef<any>;
  @ViewChild('balanceCell', { static: false }) balanceCell?: TemplateRef<any>;
  @ViewChild('actions', { static: false }) actions?: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  @ViewChild('table', { static: false }) table!: AppTableComponent<DatatableComponent>;

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Subscription Cards'));
    this.offcanvasService.closeAfterNavigationEnd();

    // Initialize toggle state from URL if present
    const hideRemovedParam = this.route.snapshot.queryParams['hideRemoved'];
    if (hideRemovedParam !== undefined) {
      this.showHideRemoved.set(hideRemovedParam === 'true');
    }
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = subscriptionCardsTableColumns;
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;

    // Set templates on columns
    for (const column of this.columns) {
      if (column.prop === 'name') column.cellTemplate = this.cardNameCell;
      if (column.prop === 'expiry_date') column.cellTemplate = this.expiryCell;
      if (column.prop === 'status') column.cellTemplate = this.statusCell;
      if (column.prop === 'balance') column.cellTemplate = this.balanceCell;
      if (column.prop === 'actions') column.cellTemplate = this.actions;
    }
    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  // Define it as a property with an arrow function to preserve 'this'
  getData = (queryParams: HttpParams): Observable<PaginatedResults<Array<Card>>> => {
    if (!this.hasSubscriptionCardsFeature()) {
      return of({
        page_size: 0,
        page: 1,
        numPages: 1,
        results: [],
      }) as unknown as Observable<PaginatedResults<Array<Card>>>;
    }

    // When a filter is applied, ignore the toggle and use a normal endpoint
    if (this.filterApplied()) {
      return this.cardsApiService.getSubscriptionCards(queryParams);
    }

    // When no filter applied, respect the toggle state
    if (this.showHideRemoved() || this.pageParam() === 'hideRemoved') {
      return this.cardsApiService.getSubscriptionCardsHideRemoved(queryParams);
    } else {
      return this.cardsApiService.getSubscriptionCards(queryParams);
    }
  };

  onToggleHideRemoved() {
    if (this.filterApplied()) return;

    this.showHideRemoved.set(!this.showHideRemoved());

    const existingParams = this.route.snapshot.queryParams;
    let queryParams = new HttpParams().appendAll(existingParams);
    queryParams = queryParams.set('hideRemoved', this.showHideRemoved().toString());

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObject(queryParams),
    });
  }

  onFilterApplied(event: boolean) {
    this.filterApplied.set(event);

    // When filter is cleared, restore the toggle behaviour
    if (!event) this.table?.reloadData();
  }

  onViewCard(id: number) {
    this.offcanvasService.offCanvasNavigate(`/cards/subscription-cards/view/${id}`);
  }

  goToRemoveCard(id: number) {
    this.offcanvasService.offCanvasNavigate(`cards/subscription-cards/remove-card/${id}`);
  }

  onCreate() {
    this.offcanvasService.offCanvasNavigate(`/cards/subscription-cards/create`);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly CardStatus = CardStatus;
}
